<template>
    <!-- Option to select in between normal and tagged attribute -->
    <div class="flex flex-column gap-2">
        <div class="flex gap-3">
            <!-- Type of Attribute i.e Choosing between Standard Attribute and Tagged Attribute -->
            <div class="flex flex-column gap-2 w-full">
                <label for="label" class="font-bold">Attribute Type: *</label>
                <Dropdown v-model="selectedAttributeType" :options="getAttributePropertyTypes()" optionLabel="name" optionValue="code" placeholder="Select attribute type" />
            </div>

            <template v-if="selectedAttributeType">
                <slot name="assessment-selection"> </slot>
                <!-- Attribute  Filter-->
                <div class="flex flex-column gap-2 w-full">
                    <label for="label" class="font-bold">{{ standardAttributeType ? 'Attribute' : 'Tag' }} *: </label>
                    <template v-if="standardAttributeType">
                        <!-- CASE 1: assessment_code filter is selected -->
                        <template v-if="link.assessment_code">
                            <Dropdown
                                id="assessmentAttributes"
                                v-model="link.attribute_code"
                                :options="attributesGroupedByBlocks?.attributes"
                                optionGroupLabel="label"
                                optionGroupChildren="items"
                                optionLabel="label"
                                optionValue="code"
                                placeholder="Select an attribute"
                                filter
                                showClear
                            />
                        </template>
                        <!-- CASE 2: assessment_code filter is not selected -->
                        <template v-else>
                            <Dropdown v-model="link.attribute_code" :options="allAttributes" filter optionLabel="label" optionValue="code" placeholder="Select an attribute" showClear />
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="link.assessment_code">
                            <Dropdown
                                id="assessmentAttributes"
                                v-model="link.tag_id"
                                :options="attributesGroupedByBlocks?.attributes"
                                optionGroupLabel="label"
                                optionGroupChildren="items"
                                optionLabel="label"
                                optionValue="tag_id"
                                placeholder="Select a tag"
                                filter
                                showClear
                            />
                        </template>
                        <template v-else>
                            <Dropdown v-model="link.tag_id" :options="allAttributes" filter optionLabel="label" optionValue="tag_id" placeholder="Select a tag" showClear />
                        </template>
                    </template>
                </div>
                <!-- Operator & Attribute Values -->
                <slot name="operator" />
                <!-- Value section -->
                <div class="flex flex-column gap-2 w-full">
                    <label for="attributeValue" class="font-bold">Value: *</label>
                    <!-- :minFractionDigits="2" -->
                    <InputText maxlength = "255" id="attributeValue" v-model="link.value" placeholder="Enter the value you expect" :class="{ 'p-invalid': isInvalid }" />
                </div>
            </template>
        </div>
        <div>
            <!-- Section for invalid messages -->
            <slot name="invalid">
                <small v-if="isInvalid">All * fields are mandatory</small>
            </slot>
        </div>
    </div>
</template>

<script setup>
// import AssessmentDropdown from './AssessmentDropdown.vue';
import { ref, onMounted, computed, watch, defineProps } from 'vue';
import { storeToRefs } from 'pinia';
import { useAttributeStore } from '../modules/attribute/store';
import { useAssessmentStore } from '../modules/assessment/store';
import { AttributePropertyTypes } from '../modules/attribute/enum';
import { getAttributePropertyTypes } from '../modules/attribute/constants';
import { UserProfileTypes } from '../../../enums';

// props
const props = defineProps({
    sourceLink: {
        type: Object,
    },
    isInvalid: {
        type: Boolean,
    },
});

// store states
const attributeStore = useAttributeStore();
const assessmentStore = useAssessmentStore();

const { params, allAttributes } = storeToRefs(attributeStore);
const { assessments } = storeToRefs(assessmentStore);

// states
const selectedAttributeType = ref(null); // only for front and procedure of attribute type case
// const activeListType = ref(null); //to compare the list type already fetch
const attributesGroupedByBlocks = ref(null);

// computed properties
const link = computed(() => props.sourceLink);
const standardAttributeType = computed(() => selectedAttributeType.value === AttributePropertyTypes.NORMAL);

watch(selectedAttributeType, () => {
    params.value = {
        ...params.value,
        user_profile: UserProfileTypes.BOTH,
        tagged: !standardAttributeType.value, //if its standard then tagged is false and vice-versa
    };
    // load attributes
    loadAttributes();
});
watch(
    () => link.value.assessment_code,
    () => {
        // load attributes by assessment
        loadAssessmentAttributes();
    },
    {
        deep: true,
    }
);

onMounted(async () => {
    // load all assessments
    if (assessments.value?.length <= 0) {
        await loadAllAssessments();
    }
    // On mounted check if the attribute is tagged type or not and load the attribute property type
    // By default, we want it standard while loading
    loadAttributePropertyType();
    // Check if there is data,
    // CASE 1:- Assessment is selected then load attributes on basis of assessment attributes
    if (link.value.assessment_code) {
        loadAssessmentAttributes();
    }
    // CASE 2:- Load attributes normally
    else loadAttributes();
});

// actions
const loadAllAssessments = async () => {
    await assessmentStore.fetchAllAssessments(true);
};
const loadAttributePropertyType = () => {
    // activeListType.value =
    selectedAttributeType.value = link.value.tag_id ? AttributePropertyTypes.TAGGED : AttributePropertyTypes.NORMAL;
};

const loadAttributes = async () => {
    // Check if attributes are already loaded, otherwise fetch new attributes
    // if (allAttributes.value?.length <= 0 && activeListType.value !== selectedAttributeType.value)
    await attributeStore.fetchAllAttributes();
};

// Load assessment attributes if any assessment is selected
const loadAssessmentAttributes = async () => {
    // standardAttributeType to toggle Standard and Tagged attributes
    attributesGroupedByBlocks.value = await assessmentStore.getAttributesGroupByBlocks(link.value.assessment_code, !standardAttributeType.value);
};
</script>
